import { createAuthedAxiosClient } from '../../api-client/api-client';

/**
 * The aggregated dashboard's Storytelling card (POST /projects/storytelling/search).
 *
 * Like the Query Tool endpoints, these are called through the shared authenticated axios client
 * rather than the generated one. The types mirror
 * backend/internal-api/tsoa-src/projects/storytelling-search-models.ts.
 */

/** Every field is optional, but a search must set at least one. Dates are `YYYY-MM-DD`. */
export interface IStorytellingSearchRequest {
  /** Free text, matched against the A3's Location or the project's address. */
  projectLocation?: string;
  /** A state_or_province id, matched against the project's address. */
  stateOrProvinceId?: string;
  clientName?: string;
  projectType?: string;
  /** A project matches when it has at least one of these. */
  facilityTypes?: string[];
  serviceTypes?: string[];
  startDateFrom?: string;
  startDateTo?: string;
  completionDateFrom?: string;
  completionDateTo?: string;
  constructionCostMin?: number;
  constructionCostMax?: number;
  deliveryMethod?: string;
  squareFootageMin?: number;
  squareFootageMax?: number;
  constructionType?: string;
}

export interface IStorytellingSearchResult {
  projectId: string;
  projectName: string;
  architectProjectNumber?: string | null;
  isArchived: boolean;
  projectLocation?: string | null;
  clientName?: string | null;
  projectType?: string | null;
  projectTypeOther?: string | null;
  startDate?: string | null;
  completionDate?: string | null;
  constructionCost?: number | null;
  deliveryMethod?: string | null;
  squareFootage?: number | null;
  constructionType?: string | null;
}

export interface IStorytellingSearchResponse {
  results: IStorytellingSearchResult[];
  /** How many projects matched, which can exceed `results.length`. */
  total: number;
}

export interface IStorytellingOptionsResponse {
  clients: string[];
}

const PATH = '/projects/storytelling';

/** The Client dropdown's values, from the projects the user has Storytelling access to. */
export const getStorytellingOptions = async (): Promise<IStorytellingOptionsResponse> => {
  const res = await createAuthedAxiosClient().get<IStorytellingOptionsResponse>(`${PATH}/options`);
  return res.data;
};

/** Whether the user has Storytelling access to at least one project. */
export const getStorytellingAccess = async (): Promise<boolean> => {
  const res = await createAuthedAxiosClient().get<{ hasAccess: boolean }>(`${PATH}/access`);
  return res.data.hasAccess;
};

export const searchStorytelling = async (
  body: IStorytellingSearchRequest,
): Promise<IStorytellingSearchResponse> => {
  const res = await createAuthedAxiosClient().post<IStorytellingSearchResponse>(
    `${PATH}/search`,
    body,
  );
  return res.data;
};
